import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PrimaryBtn } from "@/components/Common";
import { MessageCircle } from "lucide-react";
import { WHATSAPP, login } from "@/lib/api";
import Home from "@/pages/Home";
import Flights from "@/pages/Flights";
import Hotels from "@/pages/Hotels";
import Cabs from "@/pages/Cabs";
import Packages from "@/pages/Packages";
import PackageDetail from "@/pages/PackageDetail";
import Planner from "@/pages/Planner";
import MyBookings from "@/pages/MyBookings";
import Admin from "@/pages/Admin";
import { Contact, Policy } from "@/pages/Info";

const Protected = ({ children, admin }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="h-[60vh]" />;
  if (!user)
    return (
      <div className="grid h-[60vh] place-items-center text-center" data-testid="login-required">
        <div>
          <h2 className="font-display text-3xl font-bold">Please sign in</h2>
          <p className="mt-2 text-slate-500">Sign in with Google to view this page.</p>
          <PrimaryBtn className="mt-6" onClick={() => login()} data-testid="login-required-btn">Sign in with Google</PrimaryBtn>
        </div>
      </div>
    );
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  return children;
};

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollTop />
      <Navbar />
      <main className="min-h-[70vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/flights" element={<Flights />} />
          <Route path="/hotels" element={<Hotels />} />
          <Route path="/cabs" element={<Cabs />} />
          <Route path="/packages" element={<Packages />} />
          <Route path="/packages/:slug" element={<PackageDetail />} />
          <Route path="/planner" element={<Planner />} />
          <Route path="/my-bookings" element={<Protected><MyBookings /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Policy kind="terms" />} />
          <Route path="/privacy" element={<Policy kind="privacy" />} />
          <Route path="/refund" element={<Policy kind="refund" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <a href={WHATSAPP.link} target="_blank" rel="noreferrer" aria-label="Chat on WhatsApp" data-testid="whatsapp-float-btn"
        className="fixed bottom-5 right-5 z-50 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-xl shadow-black/25 transition-transform hover:scale-110">
        <MessageCircle className="h-7 w-7" />
      </a>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
